/**
 * Onboarding API Endpoints
 * Advance the user's onboarding state machine
 */

import { http } from '@/lib/http'
import { getActiveWorkspaceId } from '@/lib/api/workspace-context'
import type {
  OnboardingAdvanceRequest,
  OnboardingAdvanceResponse,
} from '@xavior/contracts'

function wsOnboarding(sub = ''): string {
  return `/workspaces/${getActiveWorkspaceId()}/onboarding${sub}`
}

/**
 * Advance the onboarding state to the next step
 */
export async function advance(
  authToken: string | null,
  body: OnboardingAdvanceRequest,
): Promise<OnboardingAdvanceResponse> {
  const response = await http.post<OnboardingAdvanceResponse>(
    wsOnboarding('/advance'),
    body,
    { authToken },
  )
  return response.data
}

export async function kickoffSync(authToken: string | null): Promise<void> {
  await http.post(wsOnboarding('/kickoff-sync'), {}, { authToken })
}

export interface SyncStatus {
  emailMessages: number
  calendarEvents: number
  driveFiles: number
  isReady: boolean
}

export async function syncStatus(authToken: string | null): Promise<SyncStatus> {
  const response = await http.get<SyncStatus>(wsOnboarding('/sync-status'), { authToken })
  return response.data
}

export interface ExtractContactsResult {
  ok: boolean
  twoWayCount: number
  created: number
  phonesFound: number
  enriched: number
  skippedExisting: number
  /** Real correspondents found but left for the Potential bucket (below the
   *  strong-seed cutoff). A lower bound — discover-contacts computes the full set. */
  surfacedToPotential?: number
}

/** Extract frequent correspondents into the Contacts module + AI-enrich the top ones. */
export async function extractContacts(authToken: string | null): Promise<ExtractContactsResult> {
  const response = await http.post<ExtractContactsResult>(
    wsOnboarding('/extract-contacts'),
    {},
    { authToken },
  )
  return response.data
}

/* ------------------------------------------------------------------ *
 * Root-mounted onboarding routes (`/onboarding/*`)
 *
 * The onboarding router is mounted both workspace-scoped
 * (`/workspaces/:id/onboarding`) and at the root (`/onboarding`). The
 * onboarding chat chips historically hit the root variant via raw `fetch()`;
 * these wrappers re-express those exact paths over `http.*` so they inherit
 * auth-refresh + 401 retry (#3268). The gateway resolves the workspace from
 * the `x-workspace-id` header `http.*` always sends.
 * ------------------------------------------------------------------ */

/** Shape returned by the root `POST /onboarding/extract-contacts`. */
export interface ExtractContactsRootResult {
  twoWayCount: number
  created: number
  phonesFound: number
  [key: string]: unknown
}

/**
 * `POST /onboarding/extract-contacts` — root-mounted variant used by the
 * onboarding chat chip. Distinct from {@link extractContacts} (the
 * workspace-scoped path) so the destination URL stays byte-identical to the
 * raw call site it replaces.
 */
export async function extractContactsRoot(
  authToken: string | null,
): Promise<ExtractContactsRootResult> {
  const response = await http.post<ExtractContactsRootResult>(
    '/onboarding/extract-contacts',
    {},
    { authToken },
  )
  return response.data
}

/** `POST /onboarding/briefing-preference` — toggle the daily-briefing flag. */
export async function setBriefingPreference(
  authToken: string | null,
  enabled: boolean,
): Promise<void> {
  await http.post('/onboarding/briefing-preference', { enabled }, { authToken })
}

export interface EmailSummaryCategory {
  label: string
  count: number
}

export interface EmailSummarySender {
  name: string
  count: number
}

export interface EmailSummary {
  totalEmails: number
  byCategory: EmailSummaryCategory[]
  topSenders: EmailSummarySender[]
  source: string
  windowDays?: number
}

/** `GET /onboarding/email-summary` — post-sync inbox analysis for narration. */
export async function getEmailSummary(authToken: string | null): Promise<EmailSummary> {
  const response = await http.get<EmailSummary>('/onboarding/email-summary', { authToken })
  return response.data
}

/**
 * `POST /workspaces/:id/onboarding/restart-discovery` — clear stale discovery
 * results from a prior demo run and kick a fresh real-data scan so personalized
 * beats reflect the user's actual world (not the demo persona's data, #3509).
 */
export async function restartDiscovery(authToken: string | null): Promise<void> {
  await http.post(wsOnboarding('/restart-discovery'), {}, { authToken })
}

export const onboarding = {
  advance,
  kickoffSync,
  syncStatus,
  extractContacts,
  extractContactsRoot,
  setBriefingPreference,
  getEmailSummary,
  restartDiscovery,
}
